import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  UrlTree
} from '@angular/router';
import { HotelService } from '../services/hotel.service';
import { DialogService } from '../services/dialog.service';
import { HotelSelectionDialogData } from '../../components/hotel-selection-dialog/hotel-selection-dialog.component';
import { UserService } from '../services/user.service';
import { ConfirmationService } from '../services/confirmation.service';
import { LoggerService } from '../services/logger.service';
import { map, catchError, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

const HOTEL_STORAGE_KEY = 'hc_selected_hotel';

@Injectable({ providedIn: 'root' })
export class HotelSelectionGuard implements CanActivate {
  constructor(
    private hotelService: HotelService,
    private dialogService: DialogService,
    private router: Router,
    private userService: UserService,
    private confirmationService: ConfirmationService,
    private logger: LoggerService,
    private translate: TranslateService
  ) {}

   
  canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const hotelId = route.paramMap.get('hotelId');

    if (!this.userService.isLoggedIn()) {
      return of(true);
    }

    // If hotelId is in route params, use it
    if (hotelId && hotelId !== 'null') {
      // Already selected - no need to re-initialize
      if (this.hotelService.hotelId === hotelId) {
        return of(true);
      }

      // Save selection and let HotelSelectionUrlGuard handle initialization
      this.saveHotelSelection(hotelId);
      return of(true);
    }

    // No hotelId in route → check if we have a selected hotel in service
    const currentHotelId = this.hotelService.hotelId;
    if (currentHotelId) {
      return of(this.router.createUrlTree(['/', currentHotelId]));
    }

    // Check for saved hotel preference in localStorage
    const savedHotelId = this.getSavedHotelSelection();
    if (savedHotelId) {
      return this.hotelService.initializeSelectedHotel(savedHotelId).pipe(
        map(() => this.router.createUrlTree(['/', savedHotelId])),
        catchError(() => {
          // Saved hotel no longer accessible, clear and continue
          this.clearHotelSelection();
          return this.checkForAutoSelection();
        })
      );
    }

    // No saved preference, check for auto-selection
    return this.checkForAutoSelection();
  }

  /**
   * Check if user has only one hotel and auto-select it,
   * otherwise show the hotel selection dialog with pre-loaded data.
   * Fetches 24 hotels (matching dialog pagination) so the dialog
   * can render immediately without a redundant API call.
   */
  private checkForAutoSelection(): Observable<boolean | UrlTree> {
    return this.hotelService.getHotels({ page: 1, per_page: 24 }).pipe(
      switchMap(response => {
        const hotels = response.data || [];
        const total = response.total || 0;

        // If user has exactly 1 hotel, auto-select it
        if (total === 1 && hotels.length === 1) {
          const hotel = hotels[0];
          const hotelId = hotel.id.toString();

          this.logger.info('Auto-selecting single hotel:', hotel.name);

          return this.hotelService.initializeSelectedHotel(hotelId).pipe(
            map(() => {
              this.saveHotelSelection(hotelId);
              return this.router.createUrlTree(['/', hotelId]);
            }),
            catchError(error => {
              this.logger.error('Failed to auto-select hotel:', error);
              if (!this.userService.isLoggedIn()) {
                return of(this.router.createUrlTree(['/account/login']));
              }
              return this.openHotelSelectionDialog();
            })
          );
        }

        // No selectable hotels — whether the user truly has none, or their
        // only hotel(s) are archived (and so excluded from this list), the
        // outcome is the same: nothing to select.
        if (total === 0) {
          this.confirmationService.swalError(this.translate.instant('notifications.guards.no_hotels'));
          return of(this.router.createUrlTree(['/account/login']));
        }

        // Multiple hotels — pass pre-loaded data to dialog to avoid redundant fetch
        return this.openHotelSelectionDialog({ hotels, total });
      }),
      catchError(error => {
        this.logger.error('Failed to fetch hotels for auto-selection:', error);
        // Session expired — do not open the dialog, auth interceptor already redirects to login
        if (!this.userService.isLoggedIn()) {
          return of(this.router.createUrlTree(['/account/login']));
        }
        return this.openHotelSelectionDialog();
      })
    );
  }

  /**
   * Open the hotel selection dialog and handle the result.
   * Accepts optional pre-loaded hotel data to avoid a redundant API call in the dialog.
   *
   * IMPORTANT: initializeSelectedHotel() must complete before we return the UrlTree.
   * Without it, HotelSelectionUrlGuard runs on the redirect target and finds the hotel
   * is neither active nor cached, so it opens the dialog a second time.
   *
   * The dialog is opened in 'guard' mode: this guard runs while the router is still
   * on the previous route (login or verify-2fa), and the UrlTree returned here is
   * what moves the app to the dashboard. The dialog must not reload the page itself,
   * or that screen stays visible until the reload finishes (HD-2013).
   */
  private openHotelSelectionDialog(preloadedData?: HotelSelectionDialogData): Observable<boolean | UrlTree> {
    return this.dialogService.openHotelSelectionDialog({ disableClose: true, preloadedData, navigation: 'guard' }).afterClosed().pipe(
      switchMap(selected => {
        if (!selected) {
          this.confirmationService.swalError(this.translate.instant('notifications.guards.select_hotel'));
          return of(this.router.createUrlTree(['/account/login']));
        }

        this.saveHotelSelection(selected);

        return this.hotelService.initializeSelectedHotel(selected).pipe(
          map(() => this.router.createUrlTree(['/', selected, 'dashboard'])),
          catchError(error => {
            this.logger.error('Failed to initialize selected hotel:', error);
            this.confirmationService.swalError(this.translate.instant('notifications.guards.select_hotel'));
            return of(this.router.createUrlTree(['/account/login']));
          })
        );
      })
    );
  }

  /**
   * Save hotel selection to localStorage for the current user
   */
  private saveHotelSelection(hotelId: string): void {
    const user = this.userService.getUser();
    if (user?.id) {
      const key = `${HOTEL_STORAGE_KEY}_${user.id}`;
      localStorage.setItem(key, hotelId);
    }
  }

  /**
   * Get saved hotel selection for the current user
   */
  private getSavedHotelSelection(): string | null {
    const user = this.userService.getUser();
    if (user?.id) {
      const key = `${HOTEL_STORAGE_KEY}_${user.id}`;
      return localStorage.getItem(key);
    }
    return null;
  }

  /**
   * Clear saved hotel selection for the current user
   */
  private clearHotelSelection(): void {
    const user = this.userService.getUser();
    if (user?.id) {
      const key = `${HOTEL_STORAGE_KEY}_${user.id}`;
      localStorage.removeItem(key);
    }
  }
}
